import React from 'react';
import { Box, TextField, FormHelperText } from '@mui/material';
import { useController, Control } from 'react-hook-form';

// Base OTP props: for uncontrolled usage
type BaseOTPProps = {
    separator?: React.ReactNode;
    length?: number;
    value: string;
    onChange: (value: string) => void;
    error?: string;
};

// Controlled OTP props: when using react-hook-form's control and name
type ControlledOTPProps = {
    name: string;
    control: Control<any>;
    separator?: React.ReactNode;
    length?: number;
};

// Union type for CustomOTP
type CustomOTPProps = ControlledOTPProps | BaseOTPProps;

// BaseOTP component: handles UI rendering
const BaseOTP: React.FC<BaseOTPProps> = ({
    separator = <span>-</span>,
    length = 6,
    value,
    onChange,
    error,
}) => {
    // Array to store input references for focusing purposes
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

    // Focus on a particular input by index
    const focusInput = (targetIndex: number) => {
        const targetInput = inputRefs.current[targetIndex];
        if (targetInput) targetInput.focus();
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('Text');
        const digits = pasted.replace(/\D/g, '').split('').slice(0, length);
        const newVal = digits.concat(Array(length - digits.length).fill('')).join('');
        onChange(newVal);
        if (digits.length > 0) focusInput(Math.min(digits.length - 1, length - 1));
      };

    // Handle key down events for navigation and deletion
    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement>,
        index: number
    ) => {
        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                if (index > 0) focusInput(index - 1);
                break;
            case 'ArrowRight':
                event.preventDefault();
                if (index < length - 1) focusInput(index + 1);
                break;
            case 'Backspace': {
                event.preventDefault();
                const otpArray = value.split('');
                otpArray[index] = '';
                onChange(otpArray.join(''));
                if (index > 0) focusInput(index - 1);
                break;
            }
            default:
                break;
        }
    };

    // Handle change in each OTP input field
    const handleChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        index: number
    ) => {
        const inputValue = event.target.value;
        // Only allow single digit numeric input
        if (!/^\d?$/.test(inputValue)) return;

        const otpArray = value.split('');
        otpArray[index] = inputValue;
        onChange(otpArray.join(''));
        if (inputValue !== '' && index < length - 1) {
            focusInput(index + 1);
        }
    };

    return (
        <Box onPaste={handlePaste}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center',}}>
                {Array.from({ length }).map((_, index) => (
                    <React.Fragment key={index}>
                        <TextField
                            label=" "
                            inputProps={{
                                style: { textAlign: 'center', width: '100%', fontSize: 24, fontWeight: 500},
                                maxLength: 1,
                            }}
                            value={value[index] || ''}
                            onChange={(e) => handleChange(e, index)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            inputRef={(el) => (inputRefs.current[index] = el)}
                        />
                        {index !== length - 1 && separator}
                    </React.Fragment>
                ))}
            </Box>
            {error && (
                <FormHelperText sx={{ mt: 1, color: 'error.main' }}>
                    {error}
                </FormHelperText>
            )}
        </Box>
    );
};

// ControlledOTP component: uses useController to manage value, onChange, error internally
const ControlledOTP: React.FC<ControlledOTPProps> = ({
    name,
    control,
    separator,
    length,
}) => {
    const { field, fieldState } = useController({ name, control });
    return (
        <BaseOTP
            separator={separator}
            length={length}
            value={field.value}
            onChange={field.onChange}
            error={fieldState.error?.message}
        />
    );
};

// CustomOTP component: renders ControlledOTP if control & name provided, else BaseOTP
const CustomOTP: React.FC<CustomOTPProps> = (props) => {
    if ('control' in props && props.control && props.name) {
        return <ControlledOTP {...props} />;
    }
    return <BaseOTP {...(props as BaseOTPProps)} />;
};

export default CustomOTP;
